import axios, { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios'
import config from '../config'
import servicesConfig from '../config/services'
import firebase from 'firebase/compat/app'
import { getJWT } from '../util/jwt_helper'
import { versions } from '../config/services'

const SandboxTestingBaseUrl =
  'https://staging.versionedservices.leadconnectorhq.com'

const serviceNameRouteMapping = {
  AuditService: 'logs/audit',
  BusinessService: '',
  CalendarService: 'calendars',
  CompanyService: 'companies',
  ContactService: 'contacts',
  ConversationChannelService: 'conversations',
  ConversationsService: 'conversations',
  CustomEmailsService: '',
  EmailBuilderService: '',
  FormServices: '',
  FunnelServices: '',
  IntegrationsServices: 'integrations',
  LinkService: 'links',
  LocationService: 'locations',
  LocationsService: 'locations',
  LogsService: 'logs',
  NotificationService: 'notifications',
  OauthService: 'oauth',
  OnboardingService: 'onboarding',
  OpportunityService: 'opportunity',
  PaymentService: '',
  PlatformService: 'platform',
  ProductService: '',
  SocialMediaService: 'social-media-posting',
  SurveyServices: '',
  SaaS: 'saas_service',
  TaskService: 'locations',
  UnblockingService: '',
  UsersService: 'users',
  WordpressService: '',
  EmailReportingService: 'reporting/emails',
  CallsReportingService: 'reporting/calls',
  SMSReportingService: 'reporting/sms',
  YextService:'yext'
}

export enum ResponseBodyTypeEnum {
  SEND_COMPLETE_RESPONSE,
  SEND_RESPONSE_DATA,
}
export default class BaseService {
  protected baseUrl: string
  protected axiosInstance: AxiosInstance
  protected secondaryInstance: AxiosInstance
  protected headers
  protected interceptor
  protected config = config
  static servicesConfig = servicesConfig
  protected responseBodyTypeEnum = ResponseBodyTypeEnum
  protected responseBodyType: ResponseBodyTypeEnum =
    ResponseBodyTypeEnum.SEND_RESPONSE_DATA
  protected useNewToken: boolean

  constructor(baseUrl: string, useNewToken: boolean = false) {
    const serviceName = this.constructor?.name // this gets the name of the child class

    this.useNewToken = useNewToken
    this.headers = {
      Channel: 'APP',
      Source: 'WEB_USER',
      Version: '2021-04-15',
    }
    if (versions[serviceName]) {
      this.headers.developer_version = versions[serviceName]
      baseUrl = `${SandboxTestingBaseUrl}/${serviceNameRouteMapping[serviceName]}`
    }

    this.baseUrl = baseUrl
    this.axiosInstance = axios.create({
      baseURL: baseUrl,
    })

    this.secondaryInstance = axios.create({
      baseURL: baseUrl,
    })

    this.interceptor = async (requestConfig: AxiosRequestConfig) => {
      try {
        const jwt = await getJWT(this.useNewToken)
        if (jwt) {
          this.headers['Authorization'] = `Bearer ${jwt}`
        }
        const firebaseToken = await firebase?.auth()?.currentUser?.getIdToken()
        requestConfig.headers = {
          ...requestConfig.headers,
          ...this.headers,
          'token-id': firebaseToken || '',
        }
      } catch (e) {}
      return requestConfig
    }

    this.axiosInstance.interceptors.request.use(this.interceptor)
  }

  protected responseBody = (res: AxiosResponse) => {
    if (
      this.responseBodyType === this.responseBodyTypeEnum.SEND_COMPLETE_RESPONSE
    ) {
      return res
    }
    return res.data
  }

  protected requests = {
    get: async (url: string, options = {}) => {
      return this.axiosInstance({
        url: this.baseUrl + url,
        method: 'GET',
        ...options,
      }).then(this.responseBody)
    },
    post: async (url: string, body: { [key: string]: any }, options = {}) =>
      this.axiosInstance({
        url: this.baseUrl + url,
        method: 'POST',
        data: body,
        ...options,
      }).then(this.responseBody),
    put: async (url: string, body: { [key: string]: any }) =>
      this.axiosInstance({
        url: this.baseUrl + url,
        method: 'PUT',
        data: body,
      }).then(this.responseBody),
    delete: async (url: string, options = {},  body: { [key: string]: any } = {}) =>
      this.axiosInstance({
        url: this.baseUrl + url,
        data:body,
        method: 'DELETE',
        ...options,
      }).then(this.responseBody),
  }
}
